// ServiceHistoryPage.js
import React, { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import moment from 'moment';
import dayjs from 'dayjs';
import { ConfigProvider, Card, Space, DatePicker, Radio, Typography, Button, Table, Input, Tooltip, Modal, Divider, List, Form, Select, message } from 'antd';
import { CheckOutlined, WarningOutlined, SearchOutlined, SyncOutlined, CalendarOutlined, FilterOutlined, LoadingOutlined, SwapOutlined, ImportOutlined } from '@ant-design/icons';

import { useConstants } from '../context/ConstantsContext';
import { useUser } from '../context/UserContext';
import { useEvents } from '../context/EventContext';
import SideMenu from '../components/SideMenu';
import { fetchServiceHistories, importServiceHistories, fetchEventDispatches, matchServiceHistories, syncServiceHistory } from '../api';
import { formatUtcDateTime } from '../utils/dateUtils';

const ServiceHistoryPage = () => {
  const constants = useConstants();
  const user = useUser();
  const { allEvents, isLoading: eventsLoading } = useEvents();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [serviceHistories, setServiceHistories] = useState([]);

  const [selectedSHRs, setSelectedSHRs] = useState([]);
  const subcontractors = useMemo(() => {
    const map = new Map();
    selectedSHRs.forEach((shr) => {
      if (shr.subcontractor_id) {
        if (!map.has(shr.subcontractor_id)) {
          map.set(shr.subcontractor_id, {
            subcontractor_id: shr.subcontractor_id,
            subcontractor_name: shr.subcontractor_name,
          });
        }
      }
    });
    return Array.from(map.values());
  }, [selectedSHRs]);

  const [syncing, setSyncing] = useState({});

  const [matchOpen, setMatchOpen] = useState(false);
  const [matchForm] = Form.useForm();
  const [matchEvent, setMatchEvent] = useState(null);
  const [selectedEventDispatches, setSelectedEventDispatches] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [dispatchesLoading, setDispatchesLoading] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importForm] = Form.useForm();
  const [importDisabled, setImportDisabled] = useState(true);
  const [importLoading, setImportLoading] = useState(false);
  const [importSummary, setImportSummary] = useState(null);

  // Load Service Histories
  const loadServiceHistories = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchServiceHistories();
      console.log(data);
      setServiceHistories(data);
    } catch (err) {
      console.error("Failed to fetch service histories", err);
      messageApi.error("Failed to fetch service histories");
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    loadServiceHistories();
  }, [loadServiceHistories]);

  // Import from Yeti
  const onImportFormChange = (changedValues, allValues) => {
    const { timeRange } = allValues;
    if (timeRange && timeRange[0]) {
      setImportDisabled(false);
    } else {
      setImportDisabled(true);
    }
  };
  
  const handleImportEventSelection = (eventId) => {
    if (eventId) {
      const selectedEvent = allEvents.find(event => event.event_id === eventId);
      if (selectedEvent) {
        importForm.setFieldsValue({
          timeRange: [dayjs(selectedEvent.event_start), dayjs(selectedEvent.event_end)]
        });
        setImportDisabled(false);
      }
    }
  };

  const renderImportForm = () => (
    <>
      <div>
        Enter a time range to import service histories from Yeti.<br />
        Selecting an event prepopulates the time range with the event time range.<br />
        Start time is requirred, the end time is optional and can be left blank to import all service histories since the start time.<br />
        Importing service histories will fetch new approved service histories from Yeti within the specified time range.<br />
      </div>
      <br />

      <Form
        form={importForm}
        layout="vertical"
        onFinish={handleYetiImport}
        onValuesChange={onImportFormChange}
      >
        <Form.Item
          label="Select Event (Optional)"
          name="event"
        >
          <Select
            placeholder="Select an Event"
            onChange={handleImportEventSelection}
            allowClear
            loading={eventsLoading}
            options={allEvents.map((event) => ({ label: event.event_name, value: event.event_id }))}
          />
        </Form.Item>

        <Form.Item
          label="Time Range"
          name="timeRange"
          rules={[
            {
              validator: (_, value) => {
                if (value && value[0]) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Start time is required"));
              }
            }
          ]}
        >
          <DatePicker.RangePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            placeholder={['Start Time', 'End Time (Optional)']}
            allowEmpty={[false, true]}
            allowClear={[true, true]}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Form>
    </>
  );

  const handleYetiImport = async (values) => {
    const { timeRange } = values;
    const startTime = timeRange[0].toISOString();
    const endTime = timeRange[1] ? timeRange[1].toISOString() : null;
  
    setImportLoading(true);
    try {
      const response = await importServiceHistories(startTime, endTime);
      setImportSummary(response);
      const data = await fetchServiceHistories();
      setServiceHistories(data);
    } catch (error) {
      console.error("Failed to import SHRs from Yeti:", error);
      messageApi.error("Failed to import SHRs from Yeti");
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportClose = () => {
    setImportSummary(null);
    setImportOpen(false);
  };

  const handleSyncServiceHistory = async (shr) => {
    setSyncing({ ...syncing, [shr.id]: true });
    try {
      const response = await syncServiceHistory(shr.id);
      console.log(response);
      if (response.status === 200) {
        messageApi.success("SHR synced and matched successfully");
      } else if (response.status === 202) {
        messageApi.warning("SHR synced successfully, but was not automatically matched");
      }
      const updatedServiceHistories = serviceHistories.map((item) => {
        if (item.id === shr.id) {
          return response.data;
        }
        return item;
      });
      setServiceHistories(updatedServiceHistories);
    } catch (error) {
      console.error("Failed to sync service history", error);
      messageApi.error("Failed to sync service history");
    } finally {
      setSyncing({ ...syncing, [shr.id]: false });
    }
  }

  // Match SHRs
  const handleMatchOpen = () => {
    setMatchOpen(true);
  };

  const handleMatchClose = () => {
    setMatchOpen(false);
    setMatchEvent(null);
    matchForm.resetFields();
  };

  const handleMatchEventSelection = async (eventId) => {
    if (eventId) {
      const selectedEvent = allEvents.find(event => event.event_id === eventId);
      if (selectedEvent) {
        setMatchEvent(selectedEvent);
        setDispatchesLoading(true);
        try {
          const dispatches = await fetchEventDispatches(eventId);
          setSelectedEventDispatches(dispatches);
        } catch (error) {
          console.error("Failed to fetch event dispatches", error);
          messageApi.error("Failed to fetch event dispatches");
        } finally {
          setDispatchesLoading(false);
        }
      }
    } else {
      setMatchEvent(null);
    }
  };

  const handleMatch = async (values) => {
    setMatchLoading(true);
    try {
      console.log('Submitted values:', values);
      let payload = {
        user_id: user.user_id,
        event_id: values.matchEvent,
        shr_ids: selectedSHRs.map(shr => shr.id),
      };
      if (matchEvent.event_type === constants.event_type.snow) {
        payload = {
          ...payload,
          dispatch_route_id: values.matchDispatchOption ? values.selectedDispatch : null,
          route_name: values.matchDispatchOption ? null : values.newRouteName,
        };
      } else if (matchEvent.event_type === constants.event_type.salt) {
        payload = {
          ...payload,
          subcontractors: values.subcontractors,
        };
      }
      const response = await matchServiceHistories(payload);
      console.log(response);
      messageApi.success("SHRs matched successfully");
      const data = await fetchServiceHistories();
      setServiceHistories(data);
      handleMatchClose();
    } catch (error) {
      console.error("Failed to match SHRs", error);
      if (error.response?.data?.error) {
        messageApi.error(`Failed to match SHRs: ${error.response.data.error}`);
      } else {
        messageApi.error("Failed to match SHRs");
      }
    } finally {
      setMatchLoading(false);
    }
  };

  // Columns with Filters & Sorting
  const columns = [
    {
      title: 'Site',
      dataIndex: 'site_name',
      key: 'site_name',
      sorter: (a, b) => a.site_name.localeCompare(b.site_name),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Site"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button onClick={confirm} type="primary" size="small">
              Search
            </Button>
            <Button onClick={clearFilters} size="small">
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#f57e1f' : undefined }} />
      ),
      onFilter: (value, record) =>
        record.site_name.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Subcontractor',
      dataIndex: 'subcontractor_name',
      key: 'subcontractor_name',
      sorter: (a, b) => a.subcontractor_name.localeCompare(b.subcontractor_name),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Subcontractor"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button onClick={confirm} type="primary" size="small">
              Search
            </Button>
            <Button onClick={clearFilters} size="small">
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#f57e1f' : undefined }} />
      ),
      onFilter: (value, record) =>
        record.subcontractor_name.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Route',
      dataIndex: 'route_name',
      key: 'route_name',
      sorter: (a, b) => a.route_name.localeCompare(b.route_name),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Route"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button onClick={confirm} type="primary" size="small">
              Search
            </Button>
            <Button onClick={clearFilters} size="small">
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#f57e1f' : undefined }} />
      ),
      onFilter: (value, record) =>
        record.route_name.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Service Time',
      key: 'service_time',
      sorter: (a, b) => moment(a.started_at).diff(moment(b.started_at)),
      filterIcon: filtered => (
        <CalendarOutlined style={{ color: filtered ? '#f57e1f' : undefined }} />
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        const [filterStart, filterEnd] = (selectedKeys[0] || [null, null]);
        return (
          <div style={{ padding: 8 }}>
            <DatePicker
              showTime
              placeholder="Start Time"
              value={filterStart}
              onChange={(date) => {
                const newRange = [date, filterEnd];
                setSelectedKeys(newRange ? [newRange] : []);
              }}
              style={{ marginBottom: 8, display: 'block' }}
            />
            <DatePicker
              showTime
              placeholder="End Time"
              value={filterEnd}
              onChange={(date) => {
                const newRange = [filterStart, date];
                setSelectedKeys(newRange ? [newRange] : []);
              }}
              style={{ marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90 }}
              >
                Filter
              </Button>
              <Button
                onClick={() => {
                  clearFilters();
                }}
                size="small"
                style={{ width: 90 }}
              >
                Reset
              </Button>
            </Space>
          </div>
        );
      },
      onFilter: (value, record) => {
        const [filterStart, filterEnd] = value || [];
      
        const filterStartMoment = filterStart ? moment(filterStart.toDate()) : null;
        const filterEndMoment = filterEnd ? moment(filterEnd.toDate()) : null;
      
        const recordStart = moment(record.started_at);
        const recordEnd = moment(record.ended_at);
      
        if (filterStartMoment && filterEndMoment) {
          return recordStart.isSameOrBefore(filterEndMoment) && recordEnd.isSameOrAfter(filterStartMoment);
        } else if (filterStartMoment && !filterEndMoment) {
          return recordStart.isSameOrAfter(filterStartMoment) || recordEnd.isSameOrAfter(filterStartMoment);
        } else if (!filterStartMoment && filterEndMoment) {
          return recordStart.isSameOrBefore(filterEndMoment) || recordEnd.isSameOrBefore(filterEndMoment);
        }
        return true;
      },
      render: record => (
        <div>
          <p>{formatUtcDateTime(record.started_at)}</p>
          <p>{formatUtcDateTime(record.ended_at)}</p>
        </div>
      ),
    },
    {
      title: 'Service Type',
      dataIndex: 'services',
      key: 'services',
      width: '10%',
      sorter: (a, b) => a.services.localeCompare(b.services),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Service Type"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button onClick={confirm} type="primary" size="small">
              Search
            </Button>
            <Button onClick={clearFilters} size="small">
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#f57e1f' : undefined }} />
      ),
      onFilter: (value, record) =>
        record.services.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Status',
      dataIndex: 'missing_data_flag',
      key: 'missing_data_flag',
      align: 'center',
      width: '10%',
      sorter: (a, b) => Number(a.missing_data_flag) - Number(b.missing_data_flag),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Radio.Group
            onChange={e =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            value={selectedKeys[0]}
            style={{ marginBottom: 8, display: 'block' }}
          >
            <Radio value="matched">Matched</Radio>
            <Radio value="not_matched">Not Matched</Radio>
          </Radio.Group>
          <Space>
            <Button onClick={confirm} type="primary" size="small">
              Filter
            </Button>
            <Button onClick={clearFilters} size="small">
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <FilterOutlined style={{ color: filtered ? '#f57e1f' : undefined }} />
      ),
      onFilter: (value, record) => {
        if (value === 'matched') {
          return record.missing_data_flag === false;
        } else if (value === 'not_matched') {
          return record.missing_data_flag === true;
        }
        return true;
      },
      render: flag =>
        flag ? (
          <WarningOutlined style={{ color: 'red' }} />
        ) : (
          <CheckOutlined style={{ color: 'green' }} />
        ),
    },
    {
      title: 'Sync',
      key: 'sync',
      align: 'center',
      width: '5%',
      render: record => (
        <Tooltip title="Sync SHR with Yeti">
          <Button
            shape='circle'
            icon={<SyncOutlined style={{ color: 'blue' }} spin={syncing[record.id]}/>}
            onClick={() => handleSyncServiceHistory(record)}
            disabled={syncing[record.id]}
          />
        </Tooltip>
      ),
    }
  ];
  
  const [tableHeight, setTableHeight] = useState('100vh - 72px - 16px');
  const titleBoxRef = useRef(null);
  const tableRef = useRef(null);

  useLayoutEffect(() => {
    const calculateTableHeight = () => {
      const headerHeight = 72;
      const titleHeight = titleBoxRef.current ? titleBoxRef.current.offsetHeight : 48;
      const padding = 16;

      const tableHeaderHeight = tableRef.current ? tableRef.current.querySelector('.ant-table-thead').offsetHeight : 55;

      const totalHeightAroundTableRows =
        headerHeight +
        titleHeight +
        padding * 4 +
        tableHeaderHeight;
      console.log(totalHeightAroundTableRows);
      setTableHeight(`calc(100vh - ${totalHeightAroundTableRows}px)`);
    };

    calculateTableHeight();

    window.addEventListener('resize', calculateTableHeight);
    return () => window.removeEventListener('resize', calculateTableHeight);
  }, []);

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#F57D20' } }}>
      {contextHolder}
      <div style={{ display: 'flex' }}>
        <SideMenu />

        <Card style={{ height: 'calc(100vh - 72px - 16px)', marginRight: '16px', overflow: 'hidden', boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)' }}>

          <div ref={titleBoxRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Typography.Title level={2} style={{ margin: 0, paddingBottom: '10px' }}>
              Service History Reports
            </Typography.Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Button
                onClick={handleMatchOpen}
                disabled={loading || !selectedSHRs.length}
              >
                Match SHRs <SwapOutlined />
              </Button>
              <Tooltip title="Import SHRs from Yeti">
                <Button
                  onClick={() => setImportOpen(true)}
                  disabled={importLoading}
                >
                  Import SHRs <ImportOutlined />
                </Button>
              </Tooltip>
            </div>
          </div>

          <div ref={tableRef} style={{ overflow: 'hidden', marginTop: '16px' }}>
            <Table
              dataSource={serviceHistories}
              loading={loading}
              columns={columns}
              rowKey={(record) => record.id}
              rowSelection={{
                type: 'checkbox',
                onChange: (selectedRowKeys, selectedRows) => {
                  setSelectedSHRs(selectedRows);
                },
              }}
              pagination={false}
              scroll={{ y: tableHeight }}
              sticky
            />
          </div>
        </Card>
      </div>

      <Modal
        title={importSummary ? "Import Summary" : "Import SHRs from Yeti"}
        open={importOpen}
        onCancel={handleImportClose}
        footer={[
          <Button key="close" onClick={handleImportClose} disabled={importLoading}>Close</Button>,
          !importSummary && <Button key="start" type="primary" onClick={() => importForm.submit()} disabled={importLoading || importDisabled}>Start SHR Import</Button>
        ]}
        style={{ top: '5vh' }}
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
      >
        {importLoading ? (
          <Space direction="horizontal" size="middle" style={{ height: '100px'}}>
            <Typography variant='text'>Importing SHRs</Typography>
            <LoadingOutlined style={{ fontSize: '32px' }} spin />
          </Space>
        ) : importSummary ? (
          <>
            <Typography>{`${importSummary.service_histories.length} SHRs imported.`}</Typography>
            {importSummary.errors.length > 0 ? (
              <Typography>{`${importSummary.errors.length} errors encountered.`}</Typography>
            ) : (
              <Typography>Import successful.</Typography>
            )}
            <Divider orientation="left">Imported SHRs</Divider>
            <List
              itemLayout="horizontal"
              dataSource={importSummary.service_histories}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <>
                        {item.site_name}
                        <br />
                        {item.subcontractor_name}
                      </>
                    }
                    description={
                      <>
                        <div>
                          <strong>Route:</strong> {item.route_name}
                        </div>
                        <div>
                          <strong>Service Time:</strong> {formatUtcDateTime(item.started_at)} – {formatUtcDateTime(item.ended_at)}
                        </div>
                        <div>
                          <strong>Service Type:</strong> {item.services}
                        </div>
                      </>
                    }
                  />
                </List.Item>
              )}
            />
            {importSummary.errors.length > 0 &&
              <>
                <Divider orientation="left">Errors</Divider>
                <List
                  itemLayout="horizontal"
                  dataSource={importSummary.errors}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        title={`${item.site_name} | ${item.sub_contractor_name}`}
                        description={
                          <>
                            <div>
                              <strong>Route:</strong> {item.route_name}
                            </div>
                            <div>
                              <strong>Service Time:</strong> {moment(item.started_at).format('YYYY-MM-DD HH:mm:ss')} – {moment(item.ended_at).format('YYYY-MM-DD HH:mm:ss')}
                            </div>
                            <div>
                              <strong>Service Type:</strong> {item.services}
                            </div>
                            <div>
                              <strong>Error:</strong> {item.error}
                            </div>
                          </>
                        }
                      />
                    </List.Item>
                  )}
                />
              </>
            }
          </>
        ) : (
          renderImportForm()
        )}
      </Modal>

      <Modal
        title="Match SHRs"
        open={matchOpen}
        onCancel={handleMatchClose}
        footer={null}
        style={{ top: '5vh' }}
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
      >
        {matchLoading ? (
          <Space direction="horizontal" size="middle" style={{ height: '100px'}}>
            <Typography variant='text'>Matching SHRs</Typography>
            <LoadingOutlined style={{ fontSize: '32px' }} spin />
          </Space>
        ) : (
          <div
            style={{ marginRight: '8px' }}
          >
            <List
              itemLayout="horizontal"
              dataSource={selectedSHRs}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <>
                        {item.site_name}
                        <br />
                        {item.subcontractor_name}
                      </>
                    }
                    description={
                      <>
                        <div>
                          <strong>Route:</strong> {item.route_name}
                        </div>
                        <div>
                          <strong>Service Time:</strong> {formatUtcDateTime(item.started_at)} – {formatUtcDateTime(item.ended_at)}
                        </div>
                        <div>
                          <strong>Service Type:</strong> {item.services}
                        </div>
                      </>
                    }
                  />
                </List.Item>
              )}
            />

            <Form form={matchForm} onFinish={handleMatch}>
              {/* Step 1: Select an Event */}
              <Divider orientation="left">Event Selection</Divider>
              <Form.Item
                name="matchEvent"
                rules={[{ required: true, message: 'Please select an event' }]}
              >
                <Select
                  placeholder="Select an event"
                  onChange={handleMatchEventSelection}
                  options={allEvents.map((event) => ({
                    label: event.event_name,
                    value: event.event_id,
                  }))}
                  allowClear
                  loading={eventsLoading}
                />
              </Form.Item>

              {/* Only render matching options if an event is selected */}
              {matchEvent && matchEvent.event_type === constants.event_type.snow && (
                <div>
                  <Divider orientation="left">Snow Event Matching</Divider>

                  <Form.Item
                    name="matchDispatchOption"
                    initialValue={true}
                    rules={[{ required: true, message: 'Please select a match option' }]}
                  >
                    <Radio.Group>
                      <Radio value={true}>Match to an Existing Dispatch</Radio>
                      <Radio value={false}>Match to a New Route</Radio>
                    </Radio.Group>
                  </Form.Item>

                  <Form.Item shouldUpdate={(prevValues, currentValues) =>
                    prevValues.matchDispatchOption !== currentValues.matchDispatchOption
                  } noStyle>
                    {({ getFieldValue }) =>
                      getFieldValue('matchDispatchOption') === true ? (
                        <Form.Item
                          name="selectedDispatch"
                          rules={[{ required: true, message: 'Please select a dispatch route' }]}
                        >
                          <Select
                            placeholder="Select a dispatch route"
                            loading={dispatchesLoading}
                            options={selectedEventDispatches.map((dispatch) => ({
                              label: dispatch.route_name,
                              value: dispatch.dispatch_route_id,
                            }))}
                          />
                        </Form.Item>
                      ) : (
                        <Form.Item
                          name="newRouteName"
                          rules={[{ required: true, message: 'Please enter a new route name' }]}
                        >
                          <Input
                            placeholder="Enter new route name"
                            allowClear
                          />
                        </Form.Item>
                      )
                    }
                  </Form.Item>
                </div>
              )}

              {matchEvent && matchEvent.event_type === constants.event_type.salt &&
                <div>
                  <Divider orientation="left">Salt Event Matching</Divider>
                  <List
                    itemLayout='vertical'
                    bordered
                  >
                    {subcontractors.map((sub) => (
                      <List.Item
                        key={sub.subcontractor_id}
                      >
                        <List.Item.Meta
                          title={sub.subcontractor_name}
                        />
                        <Form.Item
                          name={['subcontractors', String(sub.subcontractor_id), 'matchOption']}
                          initialValue={true}
                          rules={[{ required: true, message: 'Please select a match option' }]}
                        >
                          <Radio.Group>
                            <Radio value={true}>Match to an Existing Dispatch</Radio>
                            <Radio value={false}>Match to a New Route</Radio>
                          </Radio.Group>
                        </Form.Item>

                        <Form.Item
                          shouldUpdate={(prevValues, currentValues) => {
                            const prevOption =
                              prevValues.subcontractors &&
                              prevValues.subcontractors[sub.subcontractor_id] &&
                              prevValues.subcontractors[sub.subcontractor_id].matchOption;
                            const currOption =
                              currentValues.subcontractors &&
                              currentValues.subcontractors[sub.subcontractor_id] &&
                              currentValues.subcontractors[sub.subcontractor_id].matchOption;
                            return prevOption !== currOption;
                          }}
                          noStyle
                        >
                          {({ getFieldValue }) => {
                            const option = getFieldValue(['subcontractors', String(sub.subcontractor_id), 'matchOption']);
                            if (option === true) {
                              // Filter the dispatch options for this subcontractor
                              const filteredDispatches = selectedEventDispatches
                                .filter(dispatch => dispatch.subcontractor_id === sub.subcontractor_id)
                                .map(dispatch => ({
                                  label: dispatch.route_name,
                                  value: dispatch.dispatch_route_id,
                                }));
                              return (
                                <Form.Item
                                  name={['subcontractors', String(sub.subcontractor_id), 'dispatch_route_id']}
                                  rules={[{ required: true, message: 'Please select a dispatch route' }]}
                                >
                                  <Select placeholder="Select a dispatch route" options={filteredDispatches} />
                                </Form.Item>
                              );
                            } else if (option === false) {
                              return (
                                <Form.Item
                                  name={['subcontractors', String(sub.subcontractor_id), 'route_name']}
                                  rules={[{ required: true, message: 'Please enter a new route name' }]}
                                >
                                  <Input placeholder="Enter new route name" />
                                </Form.Item>
                              );
                            }
                            return null;
                          }}
                        </Form.Item>
                      </List.Item>
                    ))}

                  </List>
                </div>
              }

              <Form.Item
                style={{ display: 'flex', justifyContent: 'right', marginTop: 16 }}
              >
                <Button type="primary" htmlType="submit" loading={matchLoading}>
                  Match
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </ConfigProvider>
  );
};

export default ServiceHistoryPage;
